import { yupResolver } from '@hookform/resolvers/yup';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { StickyFormButtons } from '../../../components/Form/StickyFormButtons';
import { RendererType } from '../../../data-models/field-definition';
import { ViewModel } from '../../../models/view.model';
import { schemaToFormFields } from '../../../util/schema-utils';
import { createFormFromFieldsArray } from '../../../view-models/form.view-model';
import { showPromoteViewDialog } from './PromoteView';
import { usePromoteView } from './usePromoteView';

interface IPromoteViewFormProps {
  allViews: ViewModel[];
  selectedView: ViewModel;
}
export function PromoteViewForm({ selectedView, allViews }: IPromoteViewFormProps) {
  const { methods, onSubmit, onClose, loading } = usePromoteViewForm(selectedView, allViews);
  return (
    <FormProvider {...methods}>
      <PromoteViewFields view={selectedView} allViews={allViews} />
      <StickyFormButtons
        onSubmit={onSubmit}
        onCancel={onClose}
        style={{ padding: '0 0 2rem' }}
        submitLabel={'Promote View'}
        loading={loading}
      />
    </FormProvider>
  );
}

export function PromoteViewFields({ view, allViews }: { view: ViewModel; allViews: ViewModel[] }) {
  const schema = getPromoteViewSchema(view, allViews);
  const form = createFormFromFieldsArray(schemaToFormFields(schema));
  return <FormFactoryWithStandardLayout form={form} />;
}

export function getPromoteViewSchema(view: ViewModel, allViews: ViewModel[]) {
  const { viewType } = view;
  const viewsToReplaceOpts = allViews.filter((view) => view.isDefault && view.viewType === viewType);

  return yup.object({
    name: yup.string().required('Name is required').default('Overview'),
    viewToReplace: yup
      .number()
      .nullable()
      .optional()
      .label('Replace View')
      .formMeta({
        description: 'Select the view to replace or leave blank to add a new default view',
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: viewsToReplaceOpts.map((view) => ({
            value: view.id,
            displayName: view.name,
          })),
        },
      }),
    setAsLandingView: yup.boolean().nullable().default(true).formMeta({
      description:
        'Set this view as the default view that appears when the user navigates to Portfolio Reporting',
    }),
  });
}

export type PromoteViewFields = yup.InferType<ReturnType<typeof getPromoteViewSchema>>;

export function usePromoteViewForm(selectedView: ViewModel, allViews: ViewModel[]) {
  const promoteView = usePromoteView();
  const setShowPromoteViewDialog = useSetAtom(showPromoteViewDialog);
  const schema = getPromoteViewSchema(selectedView, allViews);
  const methods = useForm<PromoteViewFields>({
    resolver: yupResolver(schema),
    defaultValues: schema.getDefault(),
  });

  const [loading, setLoading] = useState(false);

  const { handleSubmit } = methods;
  const onSubmit = useCallback(
    async (data: PromoteViewFields) => {
      setLoading(true);
      const promotedView = await promoteView(selectedView, data);
      setLoading(false);
      if (promotedView) {
        setShowPromoteViewDialog(false);
      }
    },
    [promoteView, selectedView, setShowPromoteViewDialog]
  );

  const onClose = useCallback(() => {
    setShowPromoteViewDialog(false);
  }, [setShowPromoteViewDialog]);

  return useMemo(
    () => ({
      methods,
      onClose,
      onSubmit: handleSubmit(onSubmit),
      loading,
    }),
    [methods, handleSubmit, onSubmit, onClose, loading]
  );
}
